import { ImagePlus, Loader2, Trash2 } from 'lucide-react'
import { DragEvent, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useSignedDocumentImages } from '../../hooks/use-signed-document-images'
import { SignedDocumentImageSource, SignedDocumentType } from '../../types'

// Giới hạn giống backend (esign-image-type-not-supported / -too-large / -limit-exceeded):
// chặn sớm phía FE để khỏi upload vô ích, backend vẫn kiểm tra lại
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp']
const MAX_FILE_BYTES = 5 * 1024 * 1024
const MAX_IMAGES = 10

const formatSize = (bytes: number) =>
    bytes >= 1024 * 1024
        ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
        : `${Math.ceil(bytes / 1024)} KB`

interface ImageSourcePickerProps {
    /** Loại chứng từ có trang Hình ảnh (hiện là biên bản đối soát); hình mỗi loại độc lập */
    type: SignedDocumentType
    invoiceID: number
    value: SignedDocumentImageSource
    onChange: (value: SignedDocumentImageSource) => void
    disabled?: boolean
}

/**
 * Chọn nguồn trang "Hình ảnh" cuối chứng từ: Tự động (biểu đồ CDN / VNIS) hoặc
 * Upload hình. Chọn Upload thì hiện vùng kéo thả + danh sách hình đã upload.
 */
const ImageSourcePicker = ({
    type,
    invoiceID,
    value,
    onChange,
    disabled,
}: ImageSourcePickerProps) => {
    const { t } = useTranslation('invoice')
    const inputRef = useRef<HTMLInputElement>(null)
    const [dragging, setDragging] = useState(false)
    const { images, isLoading, upload, isUploading, remove, removingID } =
        useSignedDocumentImages(type, invoiceID)

    const handleFiles = (fileList: FileList | null) => {
        const files = Array.from(fileList ?? [])
        if (!files.length) return
        if (files.some((file) => !ACCEPTED_TYPES.includes(file.type))) {
            toast.error(
                t('signed_document_errors.esign-image-type-not-supported')
            )
            return
        }
        if (files.some((file) => file.size > MAX_FILE_BYTES)) {
            toast.error(t('signed_document_errors.esign-image-too-large'))
            return
        }
        if (images.length + files.length > MAX_IMAGES) {
            toast.error(t('signed_document_errors.esign-image-limit-exceeded'))
            return
        }
        upload(files)
    }

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        setDragging(false)
        if (!disabled && !isUploading) handleFiles(event.dataTransfer.files)
    }

    const canAddMore = images.length < MAX_IMAGES

    return (
        <div className="mb-3 rounded-md border border-gray-200 px-4 py-3 text-sm">
            <div className="flex flex-wrap items-center gap-4">
                <span className="font-medium text-gray-700">
                    {t('signed-document-image-source')}
                </span>
                {(['auto', 'upload'] as const).map((source) => (
                    <label
                        key={source}
                        className="inline-flex cursor-pointer items-center gap-1.5"
                    >
                        <input
                            type="radio"
                            name={`signed-document-image-source-${type}-${invoiceID}`}
                            checked={value === source}
                            disabled={disabled}
                            onChange={() => onChange(source)}
                        />
                        {t(`signed-document-image-source-${source}`)}
                    </label>
                ))}
            </div>
            <p className="mt-1 text-gray-500">
                {t(`signed-document-image-source-${value}-hint`)}
            </p>

            {value === 'upload' && (
                <div className="mt-3 space-y-3">
                    {canAddMore && (
                        <div
                            role="button"
                            tabIndex={0}
                            onClick={() =>
                                !disabled &&
                                !isUploading &&
                                inputRef.current?.click()
                            }
                            onKeyDown={(event) =>
                                event.key === 'Enter' &&
                                inputRef.current?.click()
                            }
                            onDragOver={(event) => {
                                event.preventDefault()
                                setDragging(true)
                            }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={handleDrop}
                            className={`flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed px-4 py-5 text-center text-gray-500 ${
                                dragging
                                    ? 'border-primary bg-blue-50'
                                    : 'border-gray-300'
                            }`}
                        >
                            {isUploading ? (
                                <Loader2 className="h-6 w-6 animate-spin" />
                            ) : (
                                <ImagePlus className="h-6 w-6" />
                            )}
                            <span>
                                {t(
                                    isUploading
                                        ? 'signed-document-image-uploading'
                                        : 'signed-document-image-drop'
                                )}
                            </span>
                            <span className="text-xs">
                                {t('signed-document-image-limits', {
                                    total: images.length,
                                    max: MAX_IMAGES,
                                })}
                            </span>
                            <input
                                ref={inputRef}
                                type="file"
                                multiple
                                accept={ACCEPTED_TYPES.join(',')}
                                className="hidden"
                                onChange={(event) => {
                                    handleFiles(event.target.files)
                                    // Cho chọn lại cùng file sau khi xoá
                                    event.target.value = ''
                                }}
                            />
                        </div>
                    )}

                    {isLoading ? (
                        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                    ) : images.length === 0 ? (
                        <p className="text-gray-500">
                            {t('signed-document-image-empty')}
                        </p>
                    ) : (
                        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                            {images.map((image) => (
                                <li
                                    key={image.image_id}
                                    className="overflow-hidden rounded-md border border-gray-200"
                                >
                                    <a
                                        href={image.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <img
                                            src={image.url}
                                            alt={image.file_name}
                                            className="h-24 w-full bg-gray-50 object-contain"
                                        />
                                    </a>
                                    <div className="flex items-center gap-2 px-2 py-1.5">
                                        <div className="min-w-0 flex-1">
                                            <div
                                                className="truncate text-xs text-gray-700"
                                                title={image.file_name}
                                            >
                                                {image.file_name}
                                            </div>
                                            <div className="text-xs text-gray-400">
                                                {formatSize(image.file_size)}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            aria-label={t(
                                                'signed-document-image-delete'
                                            )}
                                            title={t(
                                                'signed-document-image-delete'
                                            )}
                                            disabled={
                                                disabled ||
                                                removingID !== undefined
                                            }
                                            onClick={() =>
                                                remove(image.image_id)
                                            }
                                            className="text-gray-400 hover:text-red-600 disabled:opacity-50"
                                        >
                                            {removingID === image.image_id ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <Trash2 className="h-4 w-4" />
                                            )}
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    )
}

export default ImageSourcePicker
